import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Plus, Save, Trash2 } from "lucide-react";
import { api, DOC_LABELS, formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";

const TYPES = ["tax_invoice", "gold_bullion", "loan", "buying_contract"];

export default function TermsSettings() {
  const { branch } = useAuth();
  const [all, setAll] = useState({});
  const [active, setActive] = useState("tax_invoice");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api
      .get("/settings/terms", { params: { branch } })
      .then(({ data }) => setAll(data))
      .catch(() => setAll({}));
  }, [branch]);

  const sections = all[active] || [];
  const setSections = (next) => setAll({ ...all, [active]: next });

  const save = async () => {
    setSaving(true);
    try {
      await api.put("/settings/terms", { doc_type: active, terms: sections, branch });
      toast.success(`${DOC_LABELS[active]} terms saved for ${branch}`);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fade-up space-y-6" data-testid="terms-settings-page">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold sm:text-4xl">Terms &amp; Conditions</h1>
          <p className="mt-1 text-sm text-muted-foreground">
            Editing the <span className="font-semibold" data-testid="terms-branch-label">{branch}</span>{" "}
            terms. These sections print on the reverse page of every {branch} document. One
            bullet point per line.
          </p>
        </div>
        <Button
          onClick={save}
          disabled={saving}
          className="h-11 rounded-none bg-[#C5A059] px-6 font-semibold text-[#0A0A0A] hover:bg-[#B38F4E]"
          data-testid="save-terms-button"
        >
          <Save className="mr-2 h-4 w-4" /> Save terms
        </Button>
      </div>

      <div className="flex flex-wrap gap-px border border-border bg-border w-fit">
        {TYPES.map((t) => (
          <button
            key={t}
            onClick={() => setActive(t)}
            data-testid={`terms-tab-${t}`}
            className={`px-4 py-2 text-sm transition-colors duration-150 ${
              active === t ? "bg-[#0A0A0A] font-semibold text-white" : "bg-white hover:bg-[#FBF8F2]"
            }`}
          >
            {DOC_LABELS[t]}
          </button>
        ))}
      </div>

      <div className="space-y-4">
        {sections.map((section, i) => (
          <div key={i} className="border border-border p-4" data-testid={`terms-section-${i}`}>
            <div className="flex items-center gap-3">
              <Input
                className="h-10 rounded-none font-semibold"
                value={section.title}
                onChange={(e) =>
                  setSections(
                    sections.map((s, idx) =>
                      idx === i ? { ...s, title: e.target.value } : s
                    )
                  )
                }
                placeholder="Section heading e.g. 1. OWNERSHIP"
                data-testid={`terms-title-${i}`}
              />
              <button
                type="button"
                onClick={() => setSections(sections.filter((_, idx) => idx !== i))}
                className="p-2 text-muted-foreground transition-colors duration-150 hover:text-destructive"
                data-testid={`terms-delete-${i}`}
                aria-label="Delete section"
              >
                <Trash2 className="h-4 w-4" />
              </button>
            </div>
            <Textarea
              rows={Math.max(2, (section.points || []).length + 1)}
              className="mt-3 rounded-none text-sm"
              value={(section.points || []).join("\n")}
              onChange={(e) =>
                setSections(
                  sections.map((s, idx) =>
                    idx === i ? { ...s, points: e.target.value.split("\n") } : s
                  )
                )
              }
              placeholder="One bullet point per line"
              data-testid={`terms-points-${i}`}
            />
          </div>
        ))}
        <Button
          type="button"
          variant="outline"
          className="rounded-none"
          onClick={() => setSections([...sections, { title: "", points: [""] }])}
          data-testid="add-terms-section-button"
        >
          <Plus className="mr-1.5 h-4 w-4" /> Add section
        </Button>
      </div>
    </div>
  );
}
